import { useMemo } from 'react';
import {
    type TaskDefinition,
    type TaskDefinitionWorkflowNodeFragmentFragment,
    useTaskDefinitionsWorkflowQuery,
} from 'src/generated/graphql';
import { Pipeline } from '@pi/shared-utils';
import _ from 'lodash';
import toposort from 'toposort';
import { type Maybe } from '@pi/types';

export interface TaskTreeNode {
    /** used for cyclic refrences */
    id: string;
    task: Pick<TaskDefinition, '_id' | 'name' | 'type' | 'enabled'>;
    /** if this is a cyclic tree, reference to it's parent's TreeNode['id'] */
    cyclic?: string;
    /** whether this tasks was created in a loop (so multiple instances of it can be created) */
    many?: boolean;
    /** whether this task was created in an IF block (so it might not always be created) */
    optional?: boolean;
    /** how many times does this task show up */
    count: number;
    children: TaskTreeNode[];
    dependencies: TaskTreeNode[];
    dependencyDepth: number;
    dependencyName?: Maybe<string>;
    dependentIndex: number;
}

export default function useTaskTree(root: TaskDefinition['_id']): {
    loading: boolean;
    root?: TaskTreeNode;
    nodes?: Record<string, TaskTreeNode>;
} {
    const { loading, taskWorkflowNodes } = useTaskWorkflowInfo();

    return useMemo(() => {
        if (loading) return { loading: true };
        if (!taskWorkflowNodes) return { loading: false };

        const tree = buildTaskTree(taskWorkflowNodes, root);
        return {
            loading: false,
            ...tree,
        };
    }, [loading, taskWorkflowNodes, root]);
}

export const useTaskWorkflowInfo = (): {
    loading: boolean;
    taskWorkflowNodes?: Map<string, TaskDefinitionWorkflowNodeFragmentFragment>;
} => {
    const { loading, data } = useTaskDefinitionsWorkflowQuery();

    return useMemo(() => {
        if (loading) return { loading: true };

        const tasksInfo = data?.TaskDefinitions_workflow?.nodes;

        if (!tasksInfo) return { loading: false };

        return {
            loading: false,
            taskWorkflowNodes: Pipeline.from(tasksInfo).toMap(n => n._id),
        };
    }, [loading, data]);
};

const buildTaskTree = (
    tasksInfo: Map<string, TaskDefinitionWorkflowNodeFragmentFragment>,
    rootTaskDefId: TaskDefinition['_id'],
): {
    root: TaskTreeNode;
    nodes: Record<string, TaskTreeNode>;
} => {
    const nodes: Record<string, TaskTreeNode> = {};
    const taskNodeMap: Record<string, TaskTreeNode> = {};
    let idCounter = 0;
    const id = () => 'id_' + ++idCounter;

    let rootNode: TaskTreeNode;

    const iterate = (
        taskDefinitionId: string,
        parents: Record<string, TaskTreeNode>,
    ): TaskTreeNode | null => {
        const existing = parents[taskDefinitionId];

        if (parents[taskDefinitionId]) {
            return {
                id: id(),
                children: [],
                cyclic: existing.id,
                task: existing.task,
                dependencies: [],
                count: 0,
                dependencyDepth: 0,
                dependentIndex: 0,
            };
        }

        if (taskNodeMap[taskDefinitionId]) return taskNodeMap[taskDefinitionId];

        const taskInfo = tasksInfo.get(taskDefinitionId)!;
        if (!taskInfo) return null;

        const node: TaskTreeNode = {
            id: id(),
            task: _.pick(taskInfo, ['_id', 'name', 'type', 'enabled']),
            children: [],
            dependencies: [],
            count: 1,
            dependencyDepth: 0,
            dependentIndex: 0,
        };
        rootNode ||= node;
        nodes[node.id] = node;

        taskNodeMap[node.task._id] = node;

        const nodeGroups: Map<string, Array<TaskTreeNode>> = new Map();
        const getOrInitGroup = (groupName: string): TaskTreeNode[] => {
            const existing = nodeGroups.get(groupName);
            if (existing) return existing;

            const newGroup: TaskTreeNode[] = [];
            nodeGroups.set(groupName, newGroup);
            return newGroup;
        };

        const dependentCountsByGroup: Map<string, number> = new Map();
        const initOrIncrementDependentCount = (groupName: Maybe<string>) => {
            groupName ||= 'no-group';

            const existing = dependentCountsByGroup.get(groupName);
            if (typeof existing === 'number') {
                dependentCountsByGroup.set(groupName, existing + 1);
                return existing + 1;
            }
            dependentCountsByGroup.set(groupName, 0);
            return 0;
        };

        for (const childInfo of taskInfo.children) {
            const itChild = iterate(childInfo._id, {
                ...parents,
                [node.task._id]: node,
            });
            if (!itChild) continue;

            const childNode: TaskTreeNode = {
                ...itChild,
                count: childInfo.count,
                many: childInfo.many,
                optional: childInfo.optional,
                dependencies: [],
                dependencyName: childInfo.taskDependencies,
                dependentIndex: initOrIncrementDependentCount(childInfo.taskDependencies),
            };
            node.children.push(childNode);

            if (childInfo.taskGroup) {
                getOrInitGroup(childInfo.taskGroup).push(childNode);
            }
        }

        const groupDependencies = Pipeline.from(taskInfo.children)
            .filter(c => !!c.taskGroup && !!c.taskDependencies)
            .toGroupedMap(
                c => c.taskGroup!,
                c => c.taskDependencies!,
            );

        const groupNamesByAscendingDepth = toposort(
            Pipeline.from(groupDependencies.entries())
                .flatMap(([key, value]): Array<[string, string]> => value.map(v => [key, v]))
                .toArray(),
        ).reverse();

        const groupDepths = new Map<string, number>();
        for (const group of groupNamesByAscendingDepth) {
            const dependencies = groupDependencies.get(group);

            if (!dependencies) {
                groupDepths.set(group, 0);
            } else {
                let maxDepth = -1;
                for (const dep of dependencies) {
                    const depth = groupDepths.get(dep) ?? -1;
                    if (depth > maxDepth) maxDepth = depth;
                }
                groupDepths.set(group, maxDepth + 1);
            }
        }

        for (let i = 0; i < taskInfo.children.length; i++) {
            const childInfo = taskInfo.children[i];
            const childNode = node.children[i];

            const dependencyGroup = childInfo.taskDependencies
                ? nodeGroups.get(childInfo.taskDependencies)
                : null;
            if (dependencyGroup) {
                childNode.dependencies = dependencyGroup;
                childNode.dependencyDepth = (groupDepths.get(childInfo.taskDependencies!) ?? 0) + 1;
            }
        }

        node.children.sort((a, b) => a.dependencyDepth - b.dependencyDepth);

        return node;
    };

    iterate(rootTaskDefId, {});

    return {
        root: rootNode!,
        nodes,
    };
};
